import type BaseModel from "../../../src/models/BaseModel";
import type Consumer from "../../../src/models/Consumer";
import ObjectFactory from "../../../src/models/ObjectFactory";
import type { C1_EloItemInstanceData, C1_LegacyEloItemTreeData, C1_InteractiveEloPlacement } from "../types/C1_EloTypes";
import { C1_EloItem_PlugType } from "../types/C1_EloTypes";
import { isPlugChannelType } from "../../../src/models/ChannelType";
import type { ChannelType } from "../types/C1_EloTypes";
import type { C1_LightProductId } from "../data/C1_ProductDefinitions";
import { getLightProductDefinition } from "../data/C1_ProductDefinitions";
import C1_EloItem, { migrateLegacyEloItemTreeNode } from "./C1_EloItem";
import { C1_Furniture } from "./C1_Furniture";
import type { C1_FurnitureData } from "../types/C1_ModelTypes";
import type { C1_DefinitionItem } from "./C1_DefinitionItem";
import { isLightGroupDefinitionId } from "../furniture/C1_lightGroup";

/** C1 electrical data attached directly to an ESTADA Consumer. */
export class C1_Consumer extends C1_EloItem {
  constructor(data: C1_EloItemInstanceData, owner: BaseModel) {
    const { channelType, isDali, plugType, ...details } = data.details ?? {};
    super({ ...data, details }, owner);
  }

  override fromJSON(input: unknown): void {
    const data = input as C1_EloItemInstanceData;
    const { channelType, isDali, plugType, ...details } = data.details ?? {};
    super.fromJSON({ ...data, details });
  }
  private lightGroupView?: C1_Furniture;
  private lightGroupViewData?: C1_EloItemInstanceData["lightGroupFurniture"];
  private lightGroupViewPlacement?: C1_InteractiveEloPlacement;
  get consumer(): Consumer { return this.owner as Consumer; }

  get channelType(): ChannelType { return this.consumer.channelType; }
  get isDali(): boolean { return this.consumer.isDali; }

  /** Array layout is stored on the consumer; legacy furniture wrappers remain readable. */
  get lightGroup(): C1_Furniture | undefined {
    const layout = this._data.lightGroupFurniture;
    const placement = this._data.interactivePlacement;
    if (layout && placement) {
      if (this.lightGroupViewData !== layout || this.lightGroupViewPlacement !== placement) {
        // Transient furniture facade for the existing array renderer/editor.
        // Its placement is projected from the consumer, never persisted twice.
        this.lightGroupView = new C1_Furniture({
          definitionId: placement.paletteId,
          relativePosition: { ...placement.relativePosition },
          side: placement.side, _wallId: placement._wallId, wallDocked: placement.wallDocked,
          reflect: placement.reflect, masterIndex: this._data.masterIndex,
          baseProps: { ...layout.baseProps, rotation: placement.rotation },
        }, this.consumer);
        this.lightGroupViewData = layout;
        this.lightGroupViewPlacement = placement;
      }
      return this.lightGroupView;
    }
    let parent = this.parent;
    while (parent instanceof C1_EloItem) parent = parent.parent;
    return parent?.className === "C1_Furniture" && isLightGroupDefinitionId((parent as C1_Furniture).definitionId)
      ? parent as C1_Furniture : undefined;
  }

  /** Split edits from the transient furniture facade into layout and canonical placement. */
  setLightGroupFurniture(data: C1_FurnitureData): void {
    this.beforeChange();
    const { width, height, count, rows } = data.baseProps;
    this._data.lightGroupFurniture = { baseProps: { width, height, count, rows } };
    this._data.interactivePlacement = {
      paletteId: data.definitionId, relativePosition: { ...data.relativePosition },
      side: data.side, _wallId: data._wallId, wallDocked: data.wallDocked,
      rotation: Number(data.baseProps.rotation) || 0, reflect: data.reflect,
    };
    this.changed();
  }

  updateLightGroupTree(tree: C1_LegacyEloItemTreeData): void {
    const light = tree.items[0];
    if (!light) return;
    const definition = state.objectRegistry.getObject<C1_DefinitionItem>(`${tree.definitionId}/${light.definitionId}`);
    if (definition) this.definition = definition;
    this._data.details = { ...light.details };
    if (light.details?.channelType) this.setChannelType(light.details.channelType);
    if (light.details?.isDali !== undefined) this.setDali(light.details.isDali);
  }

  /** Projects current model values for the definition editor and report adapters. */
  override get data(): Readonly<C1_EloItemInstanceData> {
    const { plugType: _storedPlugType, ...details } = this._data.details ?? {};
    return { ...this._data, details: { ...details, channelType: this.channelType, isDali: this.isDali,
      plugType: isPlugChannelType(this.channelType) ? this.channelType : undefined } };
  }

  /** Consumer properties are persisted on the owner, never a second time on the facet. */
  override toJSON(): C1_EloItemInstanceData {
    const data = super.toJSON();
    const { channelType, isDali, plugType, ...details } = data.details ?? {};
    return { ...data, details };
  }

  override get className(): string {
    return "C1_Consumer";
  }

  get lightProductId(): C1_LightProductId | undefined {
    return this.data.details?.lightProductId;
  }

  get lightProductDefinition() {
    return getLightProductDefinition(this.lightProductId);
  }

  setLightProductId(lightProductId: C1_LightProductId | undefined): void {
    if (this.lightProductId === lightProductId) return;
    this.beforeChange("details");
    this._data.details = { ...this._data.details, lightProductId };
    this.changed("details");
  }

  setChannelType(channelType: ChannelType): void {
    if (this.channelType === channelType) return;
    this.consumer.changeChannelType(channelType);
  }

  setDali(value: boolean): void {
    if (this.isDali === value) return;
    this.consumer.changeDali(value);
  }

  /** Keep the socket's C1 identity in sync with its electrical supply type. */
  setSocketSwitchable(switchable: boolean): void {
    this.consumer.changeChannelType(switchable ? C1_EloItem_PlugType.V230Switch : C1_EloItem_PlugType.V230);
  }
}

ObjectFactory.registerClass("c1:C1_Consumer", (data: C1_EloItemInstanceData | C1_LegacyEloItemTreeData, owner?: BaseModel) =>
  new C1_Consumer(migrateLegacyEloItemTreeNode(data, owner!), owner!) as unknown as BaseModel,
);
